import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_STYLES = {
  RUNNING: 'bg-blue-500/20 text-blue-300',
  COMPLETE: 'bg-emerald-500/20 text-emerald-300',
  INCONCLUSIVE: 'bg-slate-600/30 text-slate-400',
};

const NewTestModal = ({ onClose, onCreated }) => {
  const [hypothesis, setHypothesis] = useState('');
  const [controlId, setControlId] = useState('');
  const [variantId, setVariantId] = useState('');
  const [spend, setSpend] = useState('500');
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setErr(null);
    setBusy(true);
    try {
      const res = await axios.post(`${API}/abh/ab-tests`, {
        hypothesis,
        control_creative_id: controlId,
        variant_creative_id: variantId,
        minimum_spend_threshold: parseFloat(spend),
      });
      onCreated(res.data?.test);
      onClose();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to create test');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="abh-new-abtest-modal"
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-slate-900 border border-slate-800 rounded-xl p-6 w-full max-w-md space-y-4"
      >
        <h3 className="text-white font-semibold">New A/B test</h3>
        <div>
          <label className="text-xs text-slate-400">Hypothesis</label>
          <input
            data-testid="abtest-hypothesis"
            value={hypothesis}
            onChange={(e) => setHypothesis(e.target.value)}
            placeholder="Testing fear-based vs curiosity-based angle"
            className="w-full mt-1 bg-slate-800 text-slate-200 text-sm rounded px-3 py-2 border border-slate-700"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400">Control creative ID</label>
          <input
            data-testid="abtest-control-id"
            value={controlId}
            onChange={(e) => setControlId(e.target.value)}
            placeholder="creative_a1b2c3"
            className="w-full mt-1 bg-slate-800 text-slate-200 text-sm rounded px-3 py-2 border border-slate-700"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400">Variant creative ID</label>
          <input
            data-testid="abtest-variant-id"
            value={variantId}
            onChange={(e) => setVariantId(e.target.value)}
            placeholder="creative_x9y8z7"
            className="w-full mt-1 bg-slate-800 text-slate-200 text-sm rounded px-3 py-2 border border-slate-700"
          />
        </div>
        <div>
          <label className="text-xs text-slate-400">Minimum spend threshold ($)</label>
          <input
            data-testid="abtest-min-spend"
            type="number"
            value={spend}
            onChange={(e) => setSpend(e.target.value)}
            className="w-full mt-1 bg-slate-800 text-slate-200 text-sm rounded px-3 py-2 border border-slate-700"
          />
        </div>
        {err && <p className="text-sm text-red-400">{err}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            className="text-sm px-3 py-1.5 rounded bg-slate-800 text-slate-300 hover:bg-slate-700"
          >
            Cancel
          </button>
          <button
            data-testid="abtest-submit"
            onClick={submit}
            disabled={!hypothesis || !controlId || !variantId || busy}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-40"
          >
            {busy ? 'Creating…' : 'Create test'}
          </button>
        </div>
      </div>
    </div>
  );
};

const ABHABTestsPage = () => {
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [evaluating, setEvaluating] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await axios.get(`${API}/abh/ab-tests`);
      setTests(res.data?.tests || []);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Unable to load A/B tests');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const evaluate = async (testId) => {
    setEvaluating(testId);
    try {
      await axios.post(`${API}/abh/ab-tests/${testId}/evaluate`);
      await load();
    } catch (e) {
      setErr(e.response?.data?.detail || 'Evaluation failed');
    } finally {
      setEvaluating(null);
    }
  };

  return (
    <div data-testid="abh-abtests-page" className="max-w-6xl mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-white text-2xl font-semibold">ABH A/B Tests</h1>
          <p className="text-slate-400 text-sm mt-1">
            Cipher picks a winner when both creatives hit the minimum spend.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            data-testid="abh-abtest-refresh"
            onClick={load}
            className="text-sm px-3 py-1.5 rounded bg-slate-800 text-slate-200 hover:bg-slate-700"
          >
            Refresh
          </button>
          <button
            data-testid="abh-abtest-new"
            onClick={() => setShowModal(true)}
            className="text-sm px-3 py-1.5 rounded bg-emerald-600 text-white hover:bg-emerald-500"
          >
            New test
          </button>
        </div>
      </div>

      {err && (
        <div className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {err}
        </div>
      )}

      {loading ? (
        <p className="text-slate-500 text-sm">Loading…</p>
      ) : tests.length === 0 ? (
        <div
          data-testid="abh-abtests-empty"
          className="bg-slate-900/60 border border-slate-800 rounded-xl p-8 text-center"
        >
          <p className="text-slate-300 font-medium">No A/B tests yet.</p>
          <p className="text-slate-500 text-sm mt-1.5">
            Create your first test to start comparing creatives.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {tests.map((t) => (
            <div
              key={t.test_id}
              data-testid={`abh-abtest-row-${t.test_id}`}
              className="bg-slate-900/40 border border-slate-800 rounded-xl p-5"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={`text-xs px-2 py-0.5 rounded ${STATUS_STYLES[t.status] || STATUS_STYLES.RUNNING}`}
                    >
                      {t.status}
                    </span>
                    {t.winner_creative_id && (
                      <span className="text-xs px-2 py-0.5 rounded bg-emerald-600 text-white">
                        Winner: {t.winner_creative_id}
                      </span>
                    )}
                  </div>
                  <p className="text-white font-medium">{t.hypothesis}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    Control: {t.control_creative_id} · Variant: {t.variant_creative_id} · Threshold ${t.minimum_spend_threshold}
                  </p>
                  {t.result_summary && (
                    <p className="text-sm text-slate-300 mt-2">{t.result_summary}</p>
                  )}
                  {t.nick_action && (
                    <p className="text-sm text-emerald-300 mt-1">→ {t.nick_action}</p>
                  )}
                </div>
                {t.status === 'RUNNING' && (
                  <button
                    data-testid={`abh-abtest-evaluate-${t.test_id}`}
                    onClick={() => evaluate(t.test_id)}
                    disabled={evaluating === t.test_id}
                    className="text-xs px-2 py-1 rounded bg-slate-800 text-slate-200 hover:bg-slate-700 disabled:opacity-40"
                  >
                    {evaluating === t.test_id ? 'Evaluating…' : 'Evaluate now'}
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <NewTestModal
          onClose={() => setShowModal(false)}
          onCreated={() => load()}
        />
      )}
    </div>
  );
};

export default ABHABTestsPage;
